import BackgroundUrl from '@assets/bg.svg';
import styled from '@emotion/styled';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { CurrentAPIAnalytics } from 'src/components/EventEditor/DataApiTab/CurrentAPIAnalytics';
import { useStore } from 'src/store/reducers';

type Params = {
  eventSlug: string;
};

const apiClient = RacemapAPIClient.fromWindowLocation();

export function CurrentAPIAnalyserView() {
  const { eventSlug } = useParams<Params>();
  const { loadCurrentEvent, clearCurrentEvent } = useStore((s) => s.events.actions);
  const isLoading = useStore((s) => s.events.isLoading);
  const currentEvent = useStore((s) => s.events.getter.currentEvent());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (eventSlug == null) return;
    let disposed = false;

    (async () => {
      try {
        const options = await apiClient.getPlayerBaseOptions(eventSlug);
        if (disposed) return;

        await loadCurrentEvent(options.eventId);
      } catch (err) {
        if (!disposed) setError(err instanceof Error ? err.message : 'Failed to load event');
      }
    })();

    return () => {
      disposed = true;
      clearCurrentEvent();
    };
  }, [eventSlug]);

  if (error != null) return <ErrorText>{error}</ErrorText>;
  if (isLoading || currentEvent == null) return null;

  return (
    <Container>
      <EventHeader>
        <EventSubtitle>Current API Analyser</EventSubtitle>
        <EventName>{currentEvent.name}</EventName>
        <EventMeta>
          {currentEvent.location && <MetaStat>{currentEvent.location}</MetaStat>}
          <MetaStat>
            {DateTime.fromISO(currentEvent.startTime).toLocaleString(DateTime.DATE_MED)}
            {' – '}
            {DateTime.fromISO(currentEvent.endTime).toLocaleString(DateTime.DATE_MED)}
          </MetaStat>
          {(currentEvent.startersCount ?? 0) > 0 && (
            <MetaStat>{currentEvent.startersCount} participants</MetaStat>
          )}
          <EventStateBadge
            isRepeating={currentEvent.isRepeating}
            startTime={currentEvent.startTime}
            endTime={currentEvent.endTime}
          />
        </EventMeta>
      </EventHeader>
      <CurrentAPIAnalytics />
    </Container>
  );
}

type EventStateBadgeProps = {
  isRepeating: boolean;
  startTime: string;
  endTime: string;
};

const badgeDescriptions: Record<BadgeVariant, string> = {
  repeating:
    'This event repeats on a loop, so it is always treated as live and keeps replaying its timeline.',
  upcoming: 'This event has not started yet — it begins at its scheduled start time.',
  replay: 'This event has finished and is now replaying its recorded data from the past.',
  live: 'This event is currently live — the current time is between its start and end time.',
};

function getEventState({ isRepeating, startTime, endTime }: EventStateBadgeProps): BadgeVariant {
  // Repeating events loop indefinitely, so they are always "live" — only show the REPEATING label.
  if (isRepeating) return 'repeating';

  const now = DateTime.now();
  if (now < DateTime.fromISO(startTime)) return 'upcoming';
  if (now > DateTime.fromISO(endTime)) return 'replay';
  return 'live';
}

function EventStateBadge(props: EventStateBadgeProps) {
  const variant = getEventState(props);

  return (
    <Badge variant={variant} title={badgeDescriptions[variant]}>
      {variant}
    </Badge>
  );
}

const Container = styled.div`
  padding: 40px;
  display: grid;
  gap: 16px;
  min-width: 0;

  > * {
    min-width: 0;
  }
  
  background-image: url(${BackgroundUrl});
`;

const ErrorText = styled.div`
  padding: 40px;
  color: #ff4d4f;
`;

const EventHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const EventSubtitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: rgba(0, 0, 0, 0.4);
`;

const EventName = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: ${RacemapColors.PaleBlue};
  line-height: 1.2;
`;

const EventMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
`;

const MetaStat = styled.span`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 10px;
  border-radius: 12px;
`;

type BadgeVariant = 'live' | 'upcoming' | 'replay' | 'repeating';

const badgeColors: Record<BadgeVariant, { color: string; background: string }> = {
  live: { color: '#237804', background: 'rgba(82, 196, 26, 0.15)' },
  upcoming: { color: '#0958d9', background: 'rgba(22, 119, 255, 0.12)' },
  replay: { color: 'rgba(0, 0, 0, 0.55)', background: 'rgba(0, 0, 0, 0.06)' },
  repeating: { color: '#08979c', background: 'rgba(19, 194, 194, 0.15)' },
};

const Badge = styled.span<{ variant: BadgeVariant }>`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  padding: 2px 10px;
  border-radius: 12px;
  cursor: help;
  color: ${({ variant }) => badgeColors[variant].color};
  background: ${({ variant }) => badgeColors[variant].background};
`;
